import {
  useDeviceSize,
  Breakpoint,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import {
  Square,
  Image,
  Flex,
  Stack,
  Section,
} from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import { Helmet } from 'react-helmet-async';
import { getWindow } from 'src/utilities/window';
import { SignupProgressTracker } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTracker';
import {
  getSignupSteps,
  STEP_CONTENT,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { SignOutButton } from 'src/components/common/features/Signup/components/SignOutButton/SignOutButton';
import SignupNavBar from 'src/components/Navigation/SignupNavBar';
import { getConfig } from 'src/utilities/config';
import { FIXED_SIGNUP_FOOTER_ID } from 'src/components/common/features/Signup/constants';
import {
  FIXED_FOOTER_HEIGHT,
  defaultWizardStepStyles,
  illustrationUrl,
  phoneFixedFooterProps,
  wizardStepEnterDuration,
  wizardStepExitDuration,
  wizardStepTransitionStyles,
} from 'src/components/selfService/features/Signup/pages/SignupWizard/SignupWizard.utils';

export function SignupWizard(): JSX.Element {
  const { breakpoint } = useDeviceSize(getWindow());
  const { activeStep, currentStepNumber } = useSignupStatus();
  const [isMounted, setIsMounted] = useState(false);
  const [currentActiveStep, setCurrentActiveStep] = useState(activeStep);
  const [wizardSignupSteps] = useState(getSignupSteps(false));
  const {
    site: { standardTitle: siteTitle },
  } = getConfig();

  const isTablet = breakpoint >= Breakpoint.sm;
  const isPhone = !isTablet;
  const footerProps = isPhone ? phoneFixedFooterProps : {};

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (currentActiveStep !== activeStep) {
      // give the exit animation time to finish before updating
      setTimeout(() => {
        setCurrentActiveStep(activeStep);
      }, wizardStepEnterDuration);
    }
  }, [activeStep, currentActiveStep]);

  return (
    <section
      className={classnames('signupWizard', {
        twoColumns: isTablet,
      })}
    >
      <Helmet defer={false}>
        <title>{`${siteTitle} | Signup - Step ${currentStepNumber}`}</title>
      </Helmet>
      <Flex
        flexDirection="column"
        backgroundColor={isPhone ? 'globals.white' : 'backgroundTint'}
        className="signupWizard_signupLeftSidebar"
      >
        <Stack y={isTablet ? 'lg' : 'none'}>
          <SignupNavBar />
        </Stack>
        <Flex
          flexGrow={isPhone ? 0 : 1}
          flexDirection="column"
          justifyContent="space-between"
          className={classnames('', {
            signupWizard_hasFixedHeader: isPhone,
          })}
        >
          <Square
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(!isTablet && { inset: 'md' })}
            overflowY={isPhone ? 'auto' : 'initial'}
            height="100%"
            paddingBottom={isPhone ? FIXED_FOOTER_HEIGHT : undefined}
            margin={isTablet ? '0 48px' : '0'}
          >
            <SignupProgressTracker steps={wizardSignupSteps} />
          </Square>
          {isTablet && (
            <Square
              width="100%"
              inset="lg"
              className="signupWizard_illustrationContainer"
            >
              <Image
                src={illustrationUrl}
                width={260}
                alt=""
                role="presentation"
              />
            </Square>
          )}
        </Flex>
      </Flex>
      <Flex
        flexDirection="column"
        height="100vh"
        className="signupWizard_signupMainContentContainer"
      >
        <Flex
          alignItems="flex-start"
          flexDirection="column"
          overflow="hidden"
          flexGrow={1}
          className={classnames('signupWizard_signupMainContent', {
            mounted: isMounted,
          })}
        >
          {isTablet && (
            <Flex
              width="100%"
              justifyContent="flex-end"
              backgroundColor="globals.white"
              dimension="2"
            >
              <Square inset="md">
                <SignOutButton />
              </Square>
            </Flex>
          )}
          {isTablet && (
            <Transition
              in={currentActiveStep === activeStep}
              timeout={{
                enter: wizardStepEnterDuration,
                exit: wizardStepExitDuration,
              }}
              mountOnEnter
              unmountOnExit
            >
              {(transitionState) => (
                <Section
                  inset="lg"
                  margin="auto"
                  width="100%"
                  overflow="auto"
                  style={{
                    ...defaultWizardStepStyles,
                    ...wizardStepTransitionStyles[transitionState],
                  }}
                >
                  {STEP_CONTENT[currentActiveStep]}
                </Section>
              )}
            </Transition>
          )}
        </Flex>
        <Square
          flexBasis="auto"
          flexGrow={0}
          flexShrink={0}
          maxHeight={FIXED_FOOTER_HEIGHT}
          id={FIXED_SIGNUP_FOOTER_ID}
          {...footerProps}
        />
      </Flex>
    </section>
  );
}
